"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { Edgeconfig, EventData } from "@types";
import { EventMode } from "@utils/enums";
import { formatPrice, joinClassNames } from "@utils/helpers";
import {
  EventConfigOverrides,
  PromoterConfigOverrides,
  WebsiteConfigOverrides,
} from "@utils/flags";
import { EventListMode } from "@features/eventListMode";
import EventCalendarMode from "@features/eventCalendarMode";
import EventInfo from "@features/eventPage/components/eventInfo";
import EventInfoAccordion from "@features/eventPage/components/eventInfoAccordion";
import calculatePriceRange from "@features/eventPage/utils/calculatePriceRange";
import EventTicketButton from "@features/eventPage/components/eventTicketButton";
import EventPastError from "@features/eventPage/components/eventPastError";
import EventDisabledError from "@features/eventPage/components/eventDisabledError";
import EventSubDescription from "@features/eventPage/components/eventSubDescription";
import EventDescription from "@features/eventPage/components/eventDescription";
import LandingPageLinkV2 from "../../../components/links/landingPageLinkV2";
import { Heading } from "@components/theming";
import StickyEventTicketButton from "../components/stickyEventTicketButton";
import TicketGate from "@features/queue/ticketGate";
import useQueue from "../utils/useQueue";

interface Props {
  event: EventData;
  edgeConfig?: Edgeconfig;
  eventConfigOverrides?: EventConfigOverrides;
  promoterConfigOverrides?: PromoterConfigOverrides;
  websiteConfigOverrides?: WebsiteConfigOverrides;
  /** Normalised event path (after /events/) used to look up queue config. */
  queueSlug?: string;
}

export default function EventDetails({
  event,
  edgeConfig,
  eventConfigOverrides,
  promoterConfigOverrides,
  websiteConfigOverrides,
  queueSlug,
}: Props) {
  const t = useTranslations("EventPage");
  const locale = useLocale();
  const searchParams = useSearchParams();

  const mainButtonRef = useRef<HTMLDivElement>(null);
  const storageKey = `event-view-${event.slug}`;

  const [showEventDescription, setShowEventDescription] = useState(false);

  const { queueState, setQueueState, queueStatus, admittedRef, opensAt } =
    useQueue(queueSlug ?? event.slug);

  useEffect(() => {
    // A `view` query param lets external links target a specific view.
    // When present it takes precedence over the stored preference; when
    // absent we fall back to the existing sessionStorage behaviour.
    const viewParam = searchParams.get("view");
    if (viewParam === "info" || viewParam === "tickets") {
      sessionStorage.setItem(storageKey, viewParam);
      setShowEventDescription(viewParam === "info");
      return;
    }
    setShowEventDescription(sessionStorage.getItem(storageKey) === "info");
  }, [storageKey, searchParams]);

  const handleToggleView = (newValue: boolean) => {
    sessionStorage.setItem(storageKey, newValue ? "info" : "tickets");
    setShowEventDescription(newValue);
  };

  const getPriceRange = useCallback(
    (ids?: { pricesectionIds?: number[]; groupedPricesectionIds?: number[] }) =>
      calculatePriceRange(event.pricing.priceschemes ?? [], ids),
    [event.pricing.priceschemes]
  );

  const priceRange = getPriceRange();

  const priceRangeText =
    event.info.pricerange ||
    `${
      priceRange.min !== priceRange.max
        ? formatPrice(locale, event.currency.code, priceRange.min) + " - "
        : ""
    }${formatPrice(locale, event.currency.code, priceRange.max)} + ${t(
      "bookingfee"
    )}`;

  const description = event.description.replaceAll(
    `href="https://www.iticket.co.nz`,
    `href="`
  );

  const disabledByPromoter =
    promoterConfigOverrides?.disableAllEvents === true ||
    (edgeConfig?.eventPageSales?.disabledByOrganisation &&
      edgeConfig?.eventPageSales.disabledByOrganisation.find(
        (uid) => uid.toLowerCase() === event.organisation.uid.toLowerCase()
      ));

  const disabledByEvent =
    eventConfigOverrides?.disableEvent === true ||
    (edgeConfig?.eventPageSales?.disabledByEvent &&
      edgeConfig?.eventPageSales.disabledByEvent.find(
        (uid) => uid.toLowerCase() === event.uid.toLowerCase()
      ));

  const disabledByWebsite = websiteConfigOverrides?.disableAllEvents === true;

  const manuallyDisabled =
    disabledByPromoter || disabledByEvent || disabledByWebsite;

  return (
    <>
      <div id="event_details_container" className="mt-4">
        <div
          ref={mainButtonRef}
          className="flex flex-col md:flex-row items-center gap-1 md:gap-4"
        >
          {event.status === "past" ? (
            <EventPastError />
          ) : manuallyDisabled ? (
            <EventDisabledError
              reason={
                disabledByWebsite
                  ? "website"
                  : disabledByPromoter
                  ? "promoter"
                  : "event"
              }
            />
          ) : (
            <EventTicketButton
              showEventDescription={showEventDescription}
              onClick={(e) => {
                e.preventDefault();
                handleToggleView(!showEventDescription);
              }}
              priceText={priceRangeText}
            />
          )}
        </div>
        {event.subdescription ? (
          <EventSubDescription subdescription={event.subdescription} />
        ) : (
          <></>
        )}
        <div
          className={joinClassNames(
            "lg:hidden",
            event.subdescription ? "mt-4" : ""
          )}
        >
          <EventInfo
            event={event}
            showEventDescription={showEventDescription}
          />
        </div>
        {!showEventDescription &&
        !manuallyDisabled &&
        event.status !== "past" ? (
          <AnimatePresence>
            <TicketGate
              eventName={event.name}
              queueState={queueState}
              setQueueState={setQueueState}
              queueStatus={queueStatus}
              admittedRef={admittedRef}
              opensAt={opensAt}
            >
              {event.settings.showingdisplaytype === EventMode.LIST ? (
                <EventListMode
                  event={event}
                  getPriceRange={getPriceRange}
                  eventConfigOverrides={eventConfigOverrides}
                />
              ) : (
                <EventCalendarMode
                  event={event}
                  getPriceRange={getPriceRange}
                />
              )}
            </TicketGate>
          </AnimatePresence>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <EventInfoAccordion
              hasPaymentInfo={event.payment.length > 0}
              priceschemes={event.pricing.priceschemes}
              discount={event.discount}
              currency={event.currency.code}
            />
            <EventDescription description={description} />
          </motion.div>
        )}
        {event.landingpages && event.landingpages.length > 0 ? (
          <div className="flex flex-col gap-4 mt-8 max-sm:mt-8">
            <Heading sharedClasses="text-lg md:text-xl font-bold">
              {t("exploremore", {
                landingpages: event.landingpages
                  .map((v, i) => {
                    return event.landingpages.length === 1
                      ? v.title
                      : event.landingpages.length === i + 2
                      ? `${v.title} & `
                      : event.landingpages.length > i + 1
                      ? `${v.title}, `
                      : v.title;
                  })
                  .join(""),
              })}
            </Heading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {event.landingpages.map((landingpage) => (
                <LandingPageLinkV2
                  key={landingpage.id}
                  slug={landingpage.url}
                  image={landingpage.images.header}
                  name={landingpage.title}
                  showNameOnMobile={event.landingpages.length > 1}
                />
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
      {!manuallyDisabled && event.status !== "past" && (
        <StickyEventTicketButton
          mainButtonRef={mainButtonRef}
          showEventDescription={showEventDescription}
          onClick={(e) => {
            e.preventDefault();
            handleToggleView(!showEventDescription);
          }}
        />
      )}
    </>
  );
}
